import React from 'react';
import { Typography } from 'antd';
import { fct } from '../../fct/tokens/tokens';

const { Text } = Typography;

/** A la llamada número 6 sin contacto la F4 pasa a online y deja de verse en el portal de Mystery (D-CX-45/79). */
export const MAX_LLAMADAS = 6;

/** Una llamada sin contacto, tal como Mystery la deja en sus notas: «dd/mm/aa no contesta — nota». */
export interface LlamadaMystery {
  /** Día de la llamada en ISO (YYYY-MM-DD), para ordenar. */
  dia: string;
  /** El día como lo escribe Mystery (dd/mm/aa). */
  fecha: string;
  nota: string | null;
}

// «01/10/26 no contesta», «1/10/2026 No contesta - buzón» o «08/10/26 no contesta — volver a llamar».
const LINEA_LLAMADA = /^\s*(\d{1,2})\/(\d{1,2})\/(\d{2}|\d{4})\s+no\s+contesta\b\s*(?:[—–-]\s*)?(.*)$/i;

const dos = (n: number) => String(n).padStart(2, '0');

/**
 * Las llamadas sin contacto que hay en las notas de Mystery (DS_NOTAS_ENCUESTADOR): una por línea «no contesta»,
 * las que registra el portal y las que Mystery escribía a mano. Las demás líneas de las notas no cuentan. De la
 * más reciente a la más antigua (a igual día, en el orden de las notas).
 */
export function llamadasDeNotas(notas: string | null | undefined): LlamadaMystery[] {
  if (!notas) return [];
  const llamadas: LlamadaMystery[] = [];
  for (const linea of notas.split(/\r?\n/)) {
    const m = LINEA_LLAMADA.exec(linea);
    if (!m) continue;
    const d = Number(m[1]);
    const mes = Number(m[2]);
    const anio = m[3].length === 2 ? 2000 + Number(m[3]) : Number(m[3]);
    if (d < 1 || d > 31 || mes < 1 || mes > 12) continue;
    const nota = m[4].trim();
    llamadas.push({ dia: `${anio}-${dos(mes)}-${dos(d)}`, fecha: `${dos(d)}/${dos(mes)}/${dos(anio % 100)}`, nota: nota || null });
  }
  return llamadas
    .map((l, i) => ({ l, i }))
    .sort((a, b) => (a.l.dia === b.l.dia ? a.i - b.i : a.l.dia < b.l.dia ? 1 : -1))
    .map(({ l }) => l);
}

/**
 * Llamadas sin contacto de una F4, de solo lectura (ficha F4 del panel): «N de 6» (el contador del sistema) y la
 * lista con la fecha y la nota de cada una, leída de las notas de Mystery.
 */
const LlamadasMystery: React.FC<{ llamadasSinExito: number; notasMystery?: string | null }> = (
  { llamadasSinExito, notasMystery },
) => {
  const llamadas = llamadasDeNotas(notasMystery);
  const agotadas = llamadasSinExito >= MAX_LLAMADAS;
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
        <span className="fct-mono" style={{ color: fct.tinta['900'] }}>{llamadasSinExito} de {MAX_LLAMADAS}</span>
        <Text type="secondary" style={{ fontSize: 12 }}>
          {agotadas
            ? 'Sexta llamada hecha: la encuesta pasa a online y Mystery ya no la ve'
            : 'A la sexta llamada sin contacto la encuesta pasa a online'}
        </Text>
      </div>
      {llamadas.length > 0 ? (
        <ol
          aria-label="Llamadas sin contacto"
          style={{
            listStyle: 'none', margin: '8px 0 0', padding: 0, border: `1px solid ${fct.linea.tarjeta}`, borderRadius: 6,
            background: fct.superficie.hundida, maxHeight: 220, overflowY: 'auto',
          }}
        >
          {llamadas.map((l, i) => (
            <li
              key={`${l.dia}-${i}`}
              style={{
                display: 'flex', gap: 12, padding: '6px 12px',
                borderTop: i === 0 ? undefined : `1px solid ${fct.linea.tarjeta}`,
              }}
            >
              <span className="fct-mono" style={{ flex: 'none', color: fct.tinta['900'] }}>{l.fecha}</span>
              <span style={{ minWidth: 0, overflowWrap: 'anywhere', color: l.nota ? fct.tinta['900'] : fct.tinta['500'] }}>
                {l.nota ?? 'No contesta'}
              </span>
            </li>
          ))}
        </ol>
      ) : (
        <div style={{ marginTop: 4 }}><Text type="secondary">Sin llamadas anotadas en las notas de Mystery</Text></div>
      )}
    </div>
  );
};

export default LlamadasMystery;
